import { AlertCircle, Check, ChevronDown, ChevronUp, Copy, RefreshCw, Send } from "lucide-react";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useIncidentReport } from "../hooks/useIncidentReport";

interface ErrorStateProps {
  code?: string;
  title?: string;
  description?: string;
  message?: string; // Backwards compat alias for description
  /**
   * @deprecated Cardinal Rule #4: never surface raw error.message / error.stack
   * to end users — backend error strings can leak operational detail or PII.
   * Pass `description="static user-facing copy"` instead and log the raw
   * error via DEV-only `console.warn` at the call site. This prop is kept
   * for backwards-compat but `error.message` is now gated to DEV-only
   * display; in production the diagnostics block stays hidden no matter
   * what the caller passes (defense-in-depth structural fix after 9+
   * codebase sites were caught with this exact leak).
   */
  error?: Error | null;
  retry?: () => void;
  onRetry?: () => void; // Backwards compat alias for retry
  action?: ReactNode;
  className?: string;
}

export function ErrorState({
  code = "Load Error",
  title = "Something went wrong",
  description,
  message,
  error,
  retry,
  onRetry,
  action,
  className = "",
}: ErrorStateProps) {
  const finalDescription = description || message || "The request could not be completed. Check your connection and try again.";
  const finalRetry = retry || onRetry;
  const [showStack, setShowStack] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timestamp = new Date().toISOString();

  useEffect(() => {
    return () => { if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current); };
  }, []);
  const { report, status: reportStatus } = useIncidentReport();

  const handleCopy = () => {
    const text = [
      `ADJUDON LOAD ERROR`,
      `CODE: ${code.toUpperCase()}`,
      `STATUS: ${error?.message || "UNKNOWN"}`,
      `TIME: ${timestamp}`,
    ].join("\n");
    // `navigator.clipboard` requires a secure context (HTTPS or localhost).
    // On HTTP / deeply nested iframes the API is undefined, in which case
    // the previous `navigator.clipboard.writeText(...).catch(() => {})`
    // would throw before reaching the catch and leave the user without
    // any indication that nothing was copied. Surface a fallback toast
    // by flipping `copied` so the visual confirmation only appears when
    // the write actually succeeded.
    if (typeof navigator === "undefined" || !navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Silent failure was the prior behaviour; preserved here because
      // the diagnostics dialog stays visible and the user can re-try
      // (clipboard permissions can be granted mid-session).
    });
  };

  const handleContactSupport = () => {
    report({ type: `error_state:${code}`, message: error?.message, timestamp });
  };

  return (
    <div className={`flex flex-col items-center justify-center py-16 px-6 text-center ${className}`}>
      {/* Icon */}
      <div className="p-3 border border-border bg-surface-1">
        <AlertCircle className="w-5 h-5 text-muted-foreground" />
      </div>

      {/* Label + Title + Description */}
      <div className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground mt-6 mb-1">
        {code}
      </div>
      <h3 className="text-sm font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground mb-6 max-w-md leading-relaxed">
        {finalDescription}
      </p>

      {/* Diagnostics — gated to DEV builds only.
          Cardinal Rule #4: production users must NEVER see raw
          `error.message` / `error.stack`, period. Even if a caller passes
          `error={...}`, the diagnostics block stays hidden in production
          — defense-in-depth structural fix that plugs the leak at the
          source so it doesn't matter how disciplined every individual
          caller is. The Copy + Contact Support actions surface enough
          for users to escalate without exposing the underlying error.
          Engineers debugging locally still see the full diagnostics. */}
      {import.meta.env.DEV && (
        <>
          <div className="border-t border-border w-full max-w-sm my-6" />
          <div className="w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground">
                Diagnostics (DEV only)
              </span>
              <button
                type="button"
                onClick={handleCopy}
                className="adp-btn flex items-center gap-1 px-2 py-1 text-[10px] text-mono uppercase tracking-wider transition-colors motion-reduce:transition-none"
              >
                <Copy className="w-3 h-3" />
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="flex flex-col gap-3 text-left">
              <div className="flex items-start gap-4">
                <span className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground w-14 flex-shrink-0 pt-px">Time</span>
                <span className="text-xs text-mono text-foreground">{timestamp}</span>
              </div>
              {error && (
                <div className="flex items-start gap-4">
                  <span className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground w-14 flex-shrink-0 pt-px">Status</span>
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="text-xs text-mono text-foreground break-words">{error.message}</span>
                    {error.stack && (
                      <>
                        <button
                          type="button"
                          onClick={() => setShowStack(v => !v)}
                          aria-expanded={showStack}
                          className="adp-btn border-transparent flex items-center gap-1 text-[10px] text-mono uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors motion-reduce:transition-none mt-1"
                        >
                          {showStack ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          Stack Trace
                        </button>
                        {showStack && (
                          <pre className="text-xs text-mono text-muted-foreground bg-surface-1 border border-border p-3 overflow-x-auto mt-1">
                            {error.stack}
                          </pre>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Actions */}
      <div className="border-t border-border w-full max-w-sm my-6" />
      <div className="flex gap-2">
        {finalRetry && (
          <button
            type="button"
            onClick={finalRetry}
            className="adp-btn flex items-center gap-1.5 px-4 py-2 text-[11px] text-mono uppercase tracking-wider transition-colors motion-reduce:transition-none"
          >
            <RefreshCw className="w-3 h-3" />
            Try Again
          </button>
        )}
        <button
          type="button"
          onClick={handleContactSupport}
          disabled={reportStatus === "sending"}
          className="adp-btn flex items-center gap-1.5 px-4 py-2 text-[11px] text-mono uppercase tracking-wider text-muted-foreground transition-colors motion-reduce:transition-none"
        >
          {reportStatus === "sent" ? <Check className="w-3 h-3" /> : <Send className="w-3 h-3" />}
          {reportStatus === "sending" ? "Sending..." : reportStatus === "sent" ? "Report Sent" : "Contact Support"}
        </button>
        {action && <div>{action}</div>}
      </div>
    </div>
  );
}
